/**
 * AG Grid column definition factories.
 * Rebuilt via useMemo when caseOTBEntrant/overnight/place/mode change.
 */

import { sailNumComparator } from "../../../global/helperFunctions";
import {
  PositionRenderer,
  FinishedSailRenderer,
  BoatCell,
  TextCell,
  PlaceRenderer,
  TimeRenderer,
  DateRenderer,
  PenaltyRenderer,
  DeleteRenderer,
  UnfinishedSailRenderer,
  NowRenderer,
} from "./CellRenderers";

export const makeFinishedCols = (caseOTBEntrant, overnight, isPlace, compactTablet) => [
  // Place mode: PLACE is the LEFT-MOST column (editable in both tabs) and the
  // position column is hidden entirely (old screen: "POS" is hide:true —
  // the place is the only ranking column).
  ...(isPlace
    ? [{
        headerName: "PLC", field: "place", width: 64, minWidth: compactTablet ? 56 : 60, maxWidth: compactTablet ? 74 : 74,
        headerClass: "nft-place-header",
        sortable: true, editable: false, suppressMovable: true,
        comparator: (a, b) => (Number(a) || 0) - (Number(b) || 0),
        cellRenderer: PlaceRenderer,
        cellRendererParams: (params) => ({ key: `pl-${params.data?.id}-${params.data?.place}` }),
        cellStyle: { display: "flex", alignItems: "center", padding: "0 4px", overflow: "visible" },
      }]
    : [{
        field: "position", headerName: "#", width: compactTablet ? 34 : 52, minWidth: compactTablet ? 32 : 52, maxWidth: compactTablet ? 38 : 60,
        sortable: true, suppressMovable: true, cellRenderer: PositionRenderer,
        comparator: (a, b) => (Number(a) || 0) - (Number(b) || 0),
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      }]),
  {
    // SAIL#: guarantee room for 8 characters before ellipsing in EVERY case.
    // The tablet min was 44px (~4 chars), which truncated "RED0293" → "RED…";
    // ~74px fits 8 chars at the tablet font.
    field: "sailNum", headerName: "SAIL#", flex: compactTablet ? 1 : 1.5, minWidth: compactTablet ? 74 : 90,
    sortable: true, suppressMovable: true, comparator: sailNumComparator,
    cellRenderer: FinishedSailRenderer,
    cellStyle: {
      display: "flex", alignItems: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    },
  },
  {
    // CLASS/BOAT trimmed slightly on tablet to free room for the wider SAIL#,
    // DATE and (unchanged) TIME columns without adding a horizontal scroll.
    field: "boatName", headerName: caseOTBEntrant ? "CLASS" : "BOAT",
    flex: compactTablet ? 1 : 2, minWidth: compactTablet ? 44 : 110, sortable: true, suppressMovable: true,
    cellRenderer: BoatCell,
    cellStyle: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center" },
  },
  {
    field: "fleetName", headerName: "FLEET", flex: compactTablet ? 0.8 : 1.5, minWidth: compactTablet ? 34 : 80,
    sortable: true, suppressMovable: true, cellRenderer: TextCell,
    cellStyle: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center" },
  },
  // Time mode only — place mode's PLACE column leads the grid instead.
  ...(isPlace
    ? []
    : [{
        headerName: "TIME", field: "hr",
        // Left-align the header and match the cell's 4px left padding so "TIME"
        // lines up with the left edge of the time box in the rows below.
        headerClass: "nft-time-header",
        // Keep the TIME column compact in ALL cases. Without a cap the
        // non-overnight column (no DATE sibling) grabbed the surplus flex space
        // and the edit-mode inputs sprawled; maxWidth pins it to roughly the
        // same footprint as the overnight column, where DATE already competes
        // for that room. The cap still fits the three HH/MM/SS edit inputs, and
        // the tablet min keeps the centered HH:MM:SS off the cell edge.
        flex: compactTablet ? 1.2 : 2,
        minWidth: compactTablet ? 76 : 120,
        maxWidth: compactTablet ? 110 : 124,
        sortable: true, editable: false, suppressMovable: true,
        valueGetter: (p) =>
          `${p.data?.hr}:${p.data?.min}:${p.data?.sec}` +
          `|${p.data?.errorHR ? 1 : 0}${p.data?.errorMin ? 1 : 0}${p.data?.errorSec ? 1 : 0}` +
          `|${p.data?.dn || ""}|${p.data?.isAbandon ? 1 : 0}`,
        // Sort by the FULL time (hr+min+sec) from row data — NOT the composite
        // value above (the comparator receives nodes, so it is unaffected).
        comparator: (a, b, nodeA, nodeB) => {
          const t = (d) =>
            (Number(d?.hr) || 0) * 3600 +
            (Number(d?.min) || 0) * 60 +
            (Number(d?.sec) || 0);
          return t(nodeA?.data) - t(nodeB?.data);
        },
        cellRenderer: TimeRenderer,
        cellRendererParams: (params) => ({
          key: `tr-${params.data?.id}-${params.data?.hr}-${params.data?.min}-${params.data?.sec}`,
        }),
        cellStyle: { display: "flex", alignItems: "center", padding: "0 4px", overflow: "visible" },
      }]),
  // Old-screen parity: place mode has no DATE column even for overnight
  // sessions (its finished table drops all time-related columns).
  ...(overnight && !isPlace
    ? [{
        headerName: "DATE", field: "finishDate", flex: compactTablet ? 1.3 : 1.4, minWidth: compactTablet ? 104 : 112,
        // Left-align the header with the cell's 5px left padding (below) so
        // "DATE" lines up with the left edge of the date input.
        headerClass: "nft-date-header",
        // ISO YYYY-MM-DD strings → lexicographic sort = chronological.
        sortable: true, editable: false, suppressMovable: true,
        // NO remount key here: DateRenderer edits in LOCAL state and commits on
        // blur/Enter/Tab; a finishDate-keyed remount destroyed the input (and
        // the native mini-calendar) mid-typing.
        cellRenderer: DateRenderer,
        // DATE only: shrink the cell's default 11px side padding to 5px so the
        // narrow column keeps its room for the input.
        cellStyle: {
          display: "flex", alignItems: "center", overflow: "visible",
          paddingLeft: "5px", paddingRight: "5px",
        },
      }]
    : []),
  {
    headerName: "PENALTY", field: "dn",
    // The value is only ever a 3-char code (e.g. WWW), so pin the column to a
    // fixed narrow width (value + dropdown arrow) instead of flexing — flex let
    // it sprawl on tablets. The width still fits the "PENALTY" header in full
    // via the nft-penalty-header class (no ellipsis).
    width: compactTablet ? 70 : 88, minWidth: compactTablet ? 66 : 84, maxWidth: compactTablet ? 78 : 96,
    headerClass: "nft-penalty-header",
    sortable: true, editable: false, suppressMovable: true,
    cellRenderer: PenaltyRenderer,
    cellStyle: { display: "flex", alignItems: "center", overflow: "visible", paddingLeft: "4px", paddingRight: "2px" },
  },
  {
    headerName: "X", field: "del", width: compactTablet ? 40 : 52, minWidth: compactTablet ? 36 : 52, maxWidth: compactTablet ? 46 : 60,
    sortable: false, editable: false, suppressMovable: true,
    cellRenderer: DeleteRenderer,
    headerClass: "ag-header-cell-center",
    cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", padding: 0 },
  },
];

export const makeUnfinishedCols = (caseOTBEntrant, mode, compactTablet) => [
  {
    // SAIL#: fit 8 chars before ellipsing even in the narrower overnight panel
    // ("RED0293" was truncating to "RED…"). Give it more flex/min and trim the
    // CLASS + FLEET columns below to make the room.
    field: "sailNum", headerName: "SAIL#", flex: compactTablet ? 1.3 : 1.5, minWidth: compactTablet ? 82 : 100,
    sortable: true, suppressMovable: true, comparator: sailNumComparator,
    cellRenderer: UnfinishedSailRenderer,
    cellStyle: { display: "flex", alignItems: "center" },
  },
  {
    field: "boatName", headerName: caseOTBEntrant ? "CLASS" : "BOAT",
    flex: compactTablet ? 1 : 2, minWidth: compactTablet ? 44 : 110, sortable: true, suppressMovable: true,
    cellRenderer: TextCell,
    cellStyle: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center" },
  },
  {
    field: "fleetName", headerName: "FLEET", flex: compactTablet ? 0.8 : 1.5, minWidth: compactTablet ? 34 : 80,
    sortable: true, suppressMovable: true, cellRenderer: TextCell,
    cellStyle: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center" },
  },
  ...(mode === "real-time"
    ? [{
        headerName: "", field: "now", width: compactTablet ? 50 : 90, minWidth: compactTablet ? 44 : 80, maxWidth: compactTablet ? 56 : 110,
        sortable: false, suppressMovable: true, cellRenderer: NowRenderer,
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      }]
    : []),
];
